/**
 * Invoice linked records strip — `prototype/sales/invoice-detail.html` ▸
 * `.linked-strip`. Each cell uses `.ic` (with `ok` / `warn` / `muted`
 * state variants) + `.copy` (with `.k` / `.v` / `.sub` 3-tier text).
 *
 * Cell composition (4):
 *   1. Buyer PO          — populated from `poReference`
 *   2. E-invoice IRN     — V1.x placeholder (NIC IRP wiring pending)
 *   3. Receipts          — count + applied total + outstanding
 *   4. Credit notes      — count + total credited, links to the CN list
 *
 * The original 4th cell was an "E-way bill" V1.x placeholder; we swapped
 * it for a real Credit notes cell because credit notes ship in V1 and
 * the operator needs a glanceable signal that the invoice has been
 * partially or fully reversed (the ribbon + linked strip + activity
 * timeline together tell the same story from three angles).
 */
import { Link } from "@tanstack/react-router";
import { Link2, FileText, FileCheck2, CreditCard, FileMinus, Printer } from "lucide-react";
import { paiseToInrShort } from "../../../lib/format.ts";
import { openDocPdf } from "../../../lib/pdf.ts";

export interface InvoiceLinkedStripProps {
  invoiceId: string;
  poReference: string | null;
  receipts: Array<{ id: string; appliedPaise: string }>;
  creditNotes: Array<{ id: string; docNumber: string; totalPaise: string }>;
  totalPaise: string;
}

/** Render compact linked records for invoice-adjacent entities. */
export function InvoiceLinkedStrip({
  invoiceId,
  poReference,
  receipts,
  creditNotes,
  totalPaise,
}: InvoiceLinkedStripProps) {
  const received = receipts.reduce((sum, row) => sum + BigInt(row.appliedPaise), 0n);
  const credited = creditNotes.reduce((sum, row) => sum + BigInt(row.totalPaise), 0n);
  const outstanding = BigInt(totalPaise) - received - credited;
  const isFullyReversed = credited >= BigInt(totalPaise);

  return (
    <section className="linked-strip" aria-label="Linked records">
      <div className="head">
        <Link2 className="ic-h" size={18} aria-hidden />
        <h3>Linked records · 4</h3>
      </div>
      <div className="grid">
        <div className="linked-cell" aria-disabled="true">
          <span className={`ic ${poReference ? "ok" : "muted"}`}>
            <FileText size={14} />
          </span>
          <div className="copy">
            <div className="k">Buyer PO</div>
            <div className="v">{poReference ?? "Not provided"}</div>
            {poReference ? <div className="sub">Reference from the buyer</div> : null}
          </div>
        </div>
        <div className="linked-cell" aria-disabled="true">
          <span className="ic muted">
            <FileCheck2 size={14} />
          </span>
          <div className="copy">
            <div className="k">E-invoice IRN</div>
            <div className="v">Pending</div>
            <div className="sub">NIC IRP wiring lands in V1.x</div>
          </div>
        </div>
        {receipts.length > 0 ? (
          <Link
            to="/sales/receipts"
            search={{ invoiceId }}
            className="linked-cell"
            aria-label={`View ${receipts.length} receipt${receipts.length === 1 ? "" : "s"} for this invoice`}
          >
            <ReceiptCellContent
              receiptCount={receipts.length}
              received={received}
              outstanding={outstanding}
            />
          </Link>
        ) : (
          <div className="linked-cell" aria-disabled="true">
            <ReceiptCellContent
              receiptCount={receipts.length}
              received={received}
              outstanding={outstanding}
            />
          </div>
        )}
        <div className="linked-cell">
          <span className={`ic ${credited > 0n ? "warn" : "muted"}`}>
            <FileMinus size={14} />
          </span>
          <div className="copy">
            <div className="k">Credit notes · {creditNotes.length}</div>
            <div className="v">
              {credited > 0n ? `${paiseToInrShort(credited.toString())} credited` : "None raised"}
            </div>
            {creditNotes.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {creditNotes.map((cn) => (
                  <button
                    key={cn.id}
                    type="button"
                    onClick={() => openDocPdf(`/api/credit-notes/${cn.id}/pdf`)}
                    title={`Print ${cn.docNumber}`}
                    className="inline-flex items-center gap-1 rounded-sm border border-line bg-paper px-1.5 py-0.5 text-xs font-medium text-ink-2 transition hover:bg-bg hover:text-ink"
                  >
                    <Printer size={11} strokeWidth={2} />
                    {cn.docNumber}
                  </button>
                ))}
              </div>
            ) : (
              <div className="sub">
                {isFullyReversed
                  ? "Invoice fully reversed"
                  : "Returns / corrections appear here"}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ReceiptCellContent({
  receiptCount,
  received,
  outstanding,
}: {
  receiptCount: number;
  received: bigint;
  outstanding: bigint;
}) {
  return (
    <>
      <span className={`ic ${received > 0n ? "ok" : "warn"}`}>
        <CreditCard size={14} />
      </span>
      <div className="copy">
        <div className="k">Receipts · {receiptCount}</div>
        <div className="v">
          {received > 0n ? `${paiseToInrShort(received.toString())} received` : "No receipts yet"}
        </div>
        <div className="sub">{paiseToInrShort(outstanding.toString())} outstanding</div>
      </div>
    </>
  );
}
